import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import React, { useState } from "react";
import { FiLock, FiMail } from "react-icons/fi";
import { supabase } from "../lib/supabase";

const MotionBox = motion(Box);

interface LoginProps {
  onSignUp: () => void;
}

export function Login({ onSignUp }: LoginProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const validateInputs = () => {
    if (!email) {
      setError("O email é obrigatório");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Por favor, insira um email válido");
      return false;
    }

    if (!password) {
      setError("A senha é obrigatória");
      return false;
    }

    if (password.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres");
      return false;
    }

    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateInputs()) {
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message === "Invalid login credentials") {
          throw new Error(
            "Email ou senha incorretos. Por favor, verifique suas credenciais."
          );
        }
        throw error;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxW="md" py={12}>
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        p={8}
        bg={bgColor}
        borderRadius="xl"
        boxShadow="xl"
        border="1px"
        borderColor={borderColor}
      >
        <VStack spacing={8}>
          <Box textAlign="center">
            <Image
              src="/Untitled.webp"
              alt="Infinity Logo"
              maxW="200px"
              mx="auto"
              mb={4}
            />
            <Text color="gray.600">Acesse sua conta para continuar</Text>
          </Box>

          <form onSubmit={handleLogin} style={{ width: "100%" }}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Email</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FiMail} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                    placeholder="Seu email"
                    disabled={loading}
                  />
                </InputGroup>
              </FormControl>

              <FormControl>
                <FormLabel>Senha</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FiLock} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                    placeholder="Sua senha"
                    disabled={loading}
                  />
                </InputGroup>
              </FormControl>

              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                size="lg"
                isLoading={loading}
                loadingText="Entrando..."
                w="100%"
              >
                Entrar
              </Button>

              <HStack justify="space-between">
                <Checkbox colorScheme="blue">Lembrar-me</Checkbox>
                <Button
                  variant="link"
                  colorScheme="blue"
                  onClick={onSignUp}
                  size="sm"
                  fontWeight="normal"
                >
                  Criar conta
                </Button>
              </HStack>
            </VStack>
          </form>
        </VStack>
      </MotionBox>
    </Container>
  );
}
